/**
 * Medal artwork — one heraldic shield per medal, drawn as inline SVG.
 *
 * The hard constraint is size: these sit beside a name in a 12.5px
 * leaderboard row, so a medal has to be identifiable at 14px across. That
 * rules out rims, bevels and fine detail, which turn to mud at that scale.
 * What survives is silhouette and colour, so the design puts everything
 * into those two: the shield outline never changes, the glyph inside is a
 * single bold form, and the metal carries the tier.
 *
 * Unearned medals are drawn in the same shape in flat slate. Visible, but
 * unmistakably not yours — the cabinet is meant to show what there is to
 * chase, which an empty slot cannot do.
 */

import { medalById } from '../../api/medals-catalogue.js';

/* Two stops each: the lit face and the shadowed lower half. Bronze through
   obsidian roughly track real alloys, except obsidian, which is deliberately
   not a metal at all — the top tier should read as something else entirely. */
const METAL_STOPS = {
  bronze: ['#d08b4a', '#7d4a1f'],
  iron: ['#b8bec7', '#6a7079'],
  silver: ['#e8eef5', '#94a2b3'],
  gold: ['#f6d477', '#b8862a'],
  platinum: ['#eaf4ff', '#8fa8c4'],
  obsidian: ['#6b5ea8', '#241d42'],
  locked: ['#2b313c', '#1b2027']
};

/** Glyph paths, drawn inside a 24×24 box centred on the shield's face. */
const GLYPHS = {
  // Pieces, as blunt silhouettes — the same reading as the captured-piece
  // strip in the HUD, not the detailed 3D sculpts.
  p: 'M12 4a3 3 0 0 1 3 3 3 3 0 0 1-1.2 2.4l.7 1.6h-5l.7-1.6A3 3 0 0 1 9 7a3 3 0 0 1 3-3zM8 12h8l-1 4H9zM7 17h10v3H7z',
  n: 'M8 20V15c0-4 2-5 3.5-6L10 7l2-3 2.5 2C17 8 18 10.5 18 14v6zM11 8.5a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8z',
  b: 'M12 3.5c1.8 1.7 3.5 3.6 3.5 5.8 0 1.6-.9 2.7-1.9 3.4l.9 3.3h-5l.9-3.3c-1-.7-1.9-1.8-1.9-3.4 0-2.2 1.7-4.1 3.5-5.8zM7.5 17h9v3h-9z',
  r: 'M6.5 4h2.2v2h2.2V4h2.2v2h2.2V4h2.2v4l-1.4 1.5.6 6.5H7.3l.6-6.5L6.5 8zM6 17h12v3H6z',
  q: 'M12 3.2a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2zM4.6 7.4l2.6 3.1.9-3.6 2.1 3.9L12 6.6l1.8 4.2 2.1-3.9.9 3.6 2.6-3.1L18 16H6zM6.5 17h11v3h-11z',
  k: 'M11 2.4h2v1.8h1.8v2H13v2.4h-2V6.2H9.2v-2H11zM5 9.6l2.6 2.4L12 9l4.4 3L19 9.6 17.6 16H6.4zM6.5 17h11v3h-11z',

  /* Campaign: crossed swords. Drawn deliberately fat — a first pass used
     realistic blade proportions and read as a faint X at 14px, which is the
     one size this medal has to survive, being the one most players see
     first. Each sword is a broad tapering blade with a visible crossguard
     and pommel, so the form holds when the detail goes. */
  swords: 'M5.4 2.6 8 2.5l8.2 9.7-2.7 2.3zM18.6 2.6 21.2 2.5l-8.2 9.7 2.7 2.3zM3.2 18.4l3.9-3.3 2.5 2.1-3.9 3.3zM20.8 18.4l-3.9-3.3-2.5 2.1 3.9 3.3zM2.4 16.2l3.2 3.8-1.5 1.3-3.2-3.8zM21.6 16.2l-3.2 3.8 1.5 1.3 3.2-3.8z',
  // Ghost Kill: a shrouded form, for the pawn that was taken in passing.
  ghost: 'M12 3.4c3.4 0 5.6 2.5 5.6 6v9.8l-1.9-1.6-1.9 1.6-1.8-1.6-1.8 1.6-1.9-1.6-1.9 1.6V9.4c0-3.5 2.2-6 5.6-6zM9.8 8.6a1.2 1.4 0 1 0 0 2.8 1.2 1.4 0 0 0 0-2.8zm4.4 0a1.2 1.4 0 1 0 0 2.8 1.2 1.4 0 0 0 0-2.8z',
  // Flawless: a shield within the shield, untouched.
  shield: 'M12 3.4l7 2.6v5.4c0 4.2-2.9 7.3-7 8.8-4.1-1.5-7-4.6-7-8.8V6z',
  // Lightning War: the obvious bolt, kept fat so it holds at 14px.
  bolt: 'M13.8 2.4L5.6 13.2h4.8l-1.4 8.4 8.6-11.2h-5.1z',
  // Headless: a crown split down the middle.
  'broken-crown': 'M3.6 7.2l3.3 2.9 2.4-4.6 1.6 3.1-1.1 2.2 1.4 6.4H6.2zM20.4 7.2l-3.3 2.9-2.4-4.6L13 8.6l1.2 2.2-1.4 6.4h4.9zM11.2 18.8h1.6l-.5 2.6h-.7z',
  // Regicide: a crown with a blade through it.
  'crossed-crown': 'M4.4 8.2l3.2 2.8L10 6.4l2 4.2 2-4.2 2.4 4.6 3.2-2.8-1.7 7.6H6.1zM6 17h12v2.6H6zM17.8 2.6l1.9 1.4-11 14.6-1.9-1.4z',
  // Usurper: the crown alone, taken whole.
  crown: 'M3.8 7l3.6 3.2L10.2 5 12 9.4 13.8 5l2.8 5.2L20.2 7l-1.9 8.4H5.7zM5.4 17h13.2v3H5.4z'
};

/* The shield outline, and a slightly inset copy used as the lit face so the
   medal reads as a struck object rather than a flat sticker. */
const SHIELD = 'M12 1.5 22 5v8.2c0 4.9-3.9 8.7-10 9.9-6.1-1.2-10-5-10-9.9V5z';
const SHIELD_FACE = 'M12 3.4 20 6.3v7c0 4-3.2 7.1-8 8.1-4.8-1-8-4.1-8-8.1v-7z';

let uid = 0;

/**
 * Renders one medal as an SVG string.
 *
 * @param {object|string} medal  a catalogue entry, or its id
 * @param {object} state  { tier, earned, online, size }
 */
export function medalSvg(medal, { tier = 0, earned = true, online = false, size = 14 } = {}) {
  const def = typeof medal === 'string' ? medalById(medal) : medal;
  if (!def) return '';

  const metal = earned ? (def.tiers[tier]?.metal ?? 'bronze') : 'locked';
  const [light, dark] = METAL_STOPS[metal] ?? METAL_STOPS.bronze;
  const glyph = GLYPHS[def.glyph] ?? GLYPHS.swords;
  const id = `m${uid++}`;

  /* The glyph is tinted rather than punched out: a hole at 14px reads as a
     smudge, whereas a darker shape on a lighter face keeps its silhouette.
     Unearned medals invert that — a pale glyph on a dark face — so the shape
     stays readable instead of disappearing into the slate. */
  const glyphFill = earned ? 'rgba(28,20,8,0.72)' : 'rgba(198,206,218,0.34)';

  return `<svg class="medal${earned ? ' is-earned' : ' is-locked'}" viewBox="0 0 24 24" width="${size}" height="${size}" role="img" aria-label="${escapeAttr(medalLabel(def, tier, earned))}">
  <defs>
    <linearGradient id="g${id}" x1="0" y1="0" x2="0.35" y2="1">
      <stop offset="0" stop-color="${light}"/>
      <stop offset="1" stop-color="${dark}"/>
    </linearGradient>
    <clipPath id="c${id}"><path d="${SHIELD_FACE}"/></clipPath>
  </defs>
  <path d="${SHIELD}" fill="${dark}"/>
  <path d="${SHIELD_FACE}" fill="url(#g${id})"/>
  <!-- Clipped to the face: several glyphs (the crossed swords especially)
       are drawn past the shield's edge on purpose so they stay bold at
       14px, and would otherwise hang outside the medal. -->
  <path d="${glyph}" fill="${glyphFill}" clip-path="url(#c${id})"/>
  ${online && earned ? `<path d="${SHIELD}" fill="none" stroke="#9fe0b4" stroke-width="1.6" opacity="0.9"/>` : ''}
</svg>`;
}

/** The medal's name with its tier, e.g. "Campaign — Veteran". */
export function medalLabel(def, tier = 0, earned = true) {
  if (!earned) return `${def.name} — not yet earned`;
  const label = def.tiers[tier]?.label;
  return label && label !== 'Earned' ? `${def.name} — ${label}` : def.name;
}

const escapeAttr = (s) => String(s).replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

/**
 * Builds a real element rather than a string, for call sites that are
 * inserting into the DOM — nothing here interpolates user input, but the
 * leaderboard does, and building elements keeps that impossible to get
 * wrong (see refreshLeaderboard).
 */
export function medalElement(medal, state = {}) {
  const wrap = document.createElement('span');
  wrap.className = 'medal-slot';
  wrap.innerHTML = medalSvg(medal, state);
  const def = typeof medal === 'string' ? medalById(medal) : medal;
  if (def) wrap.title = medalLabel(def, state.tier ?? 0, state.earned ?? true);
  return wrap;
}
